import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  Typography,
} from "@mui/material";
import { Handlers } from "../api/handlers";
import { ModalTransition } from "../../../common/components/transitions";
import StyledDialogTitle from "../../../common/components/dialog/dialog-title";
import { useForm } from "react-hook-form";
import StyledDialogActions from "../../../common/components/dialog/dialog-actions";
import { commonButtonProps } from "../../../common/components/buttons/sxprops";
import {
  UserFormSchema,
  userFormSchema,
} from "../../../common/components/user-inv-modal/api/schema";
import { zodResolver } from "@hookform/resolvers/zod";
import { UnsavedChangesDialog } from "../../../common/components/unsaved-changes-dialog";
import { useEffect } from "react";
import { enqueueSnackbar } from "notistack";
import { FieldErrors } from "react-hook-form";
import { UserForm } from "../../../common/components/user-inv-modal/components/user-form";
import useMainStore from "../../landing-page/api/store";
import useUserManagementStore from "../api/store";
import {
  useCreateBizboxUser,
  useGetUserById,
  useUpdateBizboxUser,
} from "../api/hooks";
import { pascalToRegular } from "../../../common/enums/user-roles";
import { getHighestRole } from "../../../common/utils/role-identifier";

const EMPTY_USER_FORM: UserFormSchema = {
  prefix: "",
  lastName: "",
  firstName: "",
  middleName: "",
  customName: "",
  email: "",
  civilStatus: 0,
  gender: 0,
  suffix: "",
  role: null as any,
  photoUrl: undefined as any,
  department: undefined,
};

const DUMMY_WALKTHROUGH_USER: Partial<UserFormSchema> = {
  prefix: "Ms.",
  lastName: "Santos",
  firstName: "Maria",
  middleName: "Cruz",
  email: "johndoe@mailinator.com",
  civilStatus: 0,
  gender: 1,
  role: {
    id: "UU7btIQJU3Hj4v8Epdi7078Y8SrQ6dmJGn4tetjeL2LhrcdlreuuA3QrmTyqxso/",
    name: "CoGroupAdmin",
    displayName: "Co Group Admin",
    role: "CoGroupAdmin",
  },
};

const DUMMY_WALKTHROUGH_BIZBOX_USER: Partial<UserFormSchema> = {
  prefix: "Mr.",
  lastName: "Reyes",
  firstName: "Juan",
  middleName: "Santos",
  email: "juanreyes@mailinator.com",
  civilStatus: 0,
  gender: 0,
  role: {
    id: "BIZBXAdm1nDummyWalkthroughR0l3Id000000000000000000000000000000000000",
    name: "BizboxAdmin",
    displayName: "Bizbox Admin",
    role: "BizboxAdmin",
  },
};

interface AddGroupsModalProps {
  isInternal: boolean;
  isWalkthroughMode: boolean;
  cancelUserButtonRef: React.RefObject<HTMLButtonElement>;
}

export const AddUserModal: React.FC<AddGroupsModalProps> = ({
  isInternal,
  isWalkthroughMode,
  cancelUserButtonRef,
}) => {
  const userManagementstore = useUserManagementStore();
  const { isAddModalOpen, isEditModalOpen, pendingCreateUser } =
    userManagementstore;

  const mainStore = useMainStore();
  const { data: userForEdit, isSuccess: hasLoadedUser } =
    useGetUserById(isInternal);

  const isBBUserAndUser =
    !mainStore.currentUser?.roles?.includes("SuperAdmin") &&
    !mainStore.currentUser?.roles?.includes("BizboxAdmin") &&
    !mainStore.currentUser?.roles?.includes("GroupAdmin") &&
    !mainStore.currentUser?.roles?.includes("CoGroupAdmin") &&
    !mainStore.currentUser?.roles?.includes("FacilityAdmin") &&
    !mainStore.currentUser?.roles?.includes("CoFacilityAdmin");

  const handlers = new Handlers();

  const userForm = useForm<UserFormSchema>({
    mode: "all",
    resolver: zodResolver(userFormSchema),
    defaultValues: EMPTY_USER_FORM,
  });

  const isCreateMode = isAddModalOpen && !isEditModalOpen;
  const isEditMode = isEditModalOpen && !isAddModalOpen;

  useEffect(() => {
    if (isEditMode) {
      if (hasLoadedUser && userForEdit) {
        userForm.reset({
          ...EMPTY_USER_FORM,
          ...userForEdit,
        });
      } else {
        userForm.reset(EMPTY_USER_FORM);
      }
      return;
    }

    if (isCreateMode) {
      userForm.reset({
        ...EMPTY_USER_FORM,
        ...(isWalkthroughMode
          ? (isInternal ? DUMMY_WALKTHROUGH_BIZBOX_USER : DUMMY_WALKTHROUGH_USER)
          : pendingCreateUser),
      });
    }
  }, [isCreateMode, isEditMode, hasLoadedUser, userForEdit, userForm, isWalkthroughMode]);

  const create = useCreateBizboxUser(isInternal);
  const update = useUpdateBizboxUser(isInternal);

  const onSubmit = (values: UserFormSchema) => {
    if (isCreateMode) {
      create.mutate(values);
    } else if (isEditMode) {
      update.mutate(values);
      userManagementstore.setIsEditGroupAdminModal(false);
    }
  };

  const onInvalid = (errors: FieldErrors<UserFormSchema>) => {
    const firstError = Object.values(errors)[0];
    const message =
      (firstError && "message" in firstError && (firstError.message as string)) ||
      "Please complete all required fields before saving.";
    enqueueSnackbar(message, { variant: "error" });
  };

  const hasUnsavedChanges = () => {
    return Object.keys(userForm.formState.dirtyFields).length > 0;
  };

  const handleAttemptClose = () => {
    if (hasUnsavedChanges()) {
      handlers.handleOpenUnsavedChangesDialogStore();
    } else {
      handlers.handleCloseModal();
    }
  };

  const CGAccess =
    "Co Group Admin" ===
    (mainStore.currentUser?.roles &&
      pascalToRegular(getHighestRole(mainStore.currentUser?.roles)));

  const CSAccess =
    "Bizbox Admin" ===
    (mainStore.currentUser?.roles &&
      pascalToRegular(getHighestRole(mainStore.currentUser?.roles)));

  return (
    <Dialog
      onClose={handleAttemptClose}
      open={isCreateMode || isEditMode}
      hideBackdrop={isWalkthroughMode}
      slots={{
        transition: ModalTransition,
      }}
      slotProps={{
        paper: {
          component: "form",
          "data-testid": "group-admin-user-dialog",
          onSubmit: userForm.handleSubmit(onSubmit, onInvalid),
          sx: {
            borderRadius: "24px",
            ...(isWalkthroughMode && {
              width: "calc(100vw - 320px)",
              maxWidth: "calc(100vw - 320px)",
              m: 0,
            }),
          },
        } as any,
      }}
      maxWidth={isWalkthroughMode ? false : "laptop"}
      fullWidth={!isWalkthroughMode}
      aria-describedby="alert-dialog-slide-description"
      sx={{
        zIndex: isWalkthroughMode ? (theme) => theme.zIndex.modal - 2 : null,
        ...(isWalkthroughMode && {
          "& .MuiDialog-container": {
            justifyContent: "flex-end",
            alignItems: "flex-start",
            pt: "40px",
            pr: "16px",
          },
        }),
      }}
    >
      <Box id={"group-admin-user-dialog"}>
        <StyledDialogTitle
          actionType="close"
          testIdPrefix="group-admin-user-modal"
          title="User Details"
          subtitle="Provide additional information related to the user."
          handleClose={handleAttemptClose}
        />

        <DialogContent sx={{ minWidth: "50%" }}>
          <Box>
            <UserForm
              isLoading={create.isPending || update.isPending}
              walkthroughMode={isWalkthroughMode}
              variant={isEditMode ? "userManage:update" : "userManage:create"}
              userForm={userForm}
              showDepartment={isInternal}
              disabled={
                isBBUserAndUser ||
                CGAccess ||
                (CSAccess && userForEdit?.role.displayName === "Bizbox Admin")
              }
              onChangeFacilityAdmin={handlers.handleChangeFacilityAdmin}
            />
          </Box>
        </DialogContent>

        <StyledDialogActions>
          {isCreateMode && (
            <Typography
              variant="caption"
              color="secondary.main"
              fontWeight={400}
              sx={{ mr: "auto" }}
            >
              The Co-Admin login details will be sent to the provided email
              address.
            </Typography>
          )}

          <Button
            id="group-admin-user-modal-cancel-button"
            data-testid="group-admin-user-modal-cancel-button"
            ref={cancelUserButtonRef}
            onClick={handleAttemptClose}
            disabled={create.isPending || update.isPending}
          >
            Cancel
          </Button>
          <Button
            id="group-admin-user-modal-save-button"
            data-testid="group-admin-user-modal-save-button"
            disabled={CGAccess || create.isPending || update.isPending}
            loading={create.isPending || update.isPending}
            type="submit"
            sx={{
              ...commonButtonProps,
              backgroundColor: "primary.main",
              ml: 1,
            }}
          >
            {create.isPending || update.isPending ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              "Save"
            )}
          </Button>
        </StyledDialogActions>

        <UnsavedChangesDialog
          onContinue={() => {
            userForm.reset(EMPTY_USER_FORM);
            handlers.handleCloseModal();
          }}
        />
      </Box>
    </Dialog>
  );
};
